import React from 'react';
import { BrowserRouter, Routes, Route, Navigate } from 'react-router-dom';
import { AuthProvider } from './contexts/AuthContext';
import { FloatingVideoProvider } from './contexts/FloatingVideoContext';
import { ChatProvider } from './contexts/ChatContext';
import { NotificationProvider } from './contexts/NotificationContext';
import { AdminProvider, useAdmin } from './contexts/AdminContext';
import { ToastProvider } from './components/Toast';
import Layout from './components/Layout';
import AdminLayout from './components/AdminLayout';
import ProtectedRoute from './components/ProtectedRoute';
import FloatingVideoPlayer from './components/FloatingVideoPlayer';
import AppLoadingScreen from './components/AppLoadingScreen';
import ChatKit from './components/CallKit';
import useAppInitializer from './hooks/useAppInitializer';
import Login from './pages/Login';
import Register from './pages/Register';
import Home from './pages/Home';
import Profile from './pages/Profile';
import Friends from './pages/Friends';
import Chat from './pages/Chat';
import Post from './pages/Post';
import Search from './pages/Search';
import Notifications from './pages/Notifications';
import Terms from './pages/Terms';
import Privacy from './pages/Privacy';
import AdminDashboard from './components/AdminDashboard';
import AdminUsers from './components/AdminUsers';
import AdminGroups from './components/AdminGroups';
import UserDetail from './components/UserDetail';
import './i18n/config';

// Protected admin route component
function AdminProtectedRoute({ children }) {
  const { hasDashboard, loading } = useAdmin();

  if (loading) {
    return (
      <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', height: '100vh' }}>
        Loading...
      </div>
    );
  }

  if (!hasDashboard) {
    return <Navigate to="/" replace />;
  }

  return children;
}

function AppRoutes() {
  const { isInitializing, retry } = useAppInitializer();

  return (
    <>
      <AppLoadingScreen isInitializing={isInitializing} onRetry={retry} />
      <Routes>
        <Route path="/login" element={<Login />} />
        <Route path="/register" element={<Register />} />
        <Route
          path="/terms"
          element={
            <ProtectedRoute>
              <Layout>
                <Terms />
              </Layout>
            </ProtectedRoute>
          }
        />
        <Route
          path="/privacy"
          element={
            <ProtectedRoute>
              <Layout>
                <Privacy />
              </Layout>
            </ProtectedRoute>
          }
        />

        <Route
          path="/"
          element={
            <ProtectedRoute>
              <Layout>
                <Home />
              </Layout>
            </ProtectedRoute>
          }
        />

        <Route
          path="/profile/:username"
          element={
            <ProtectedRoute>
              <Layout>
                <Profile />
              </Layout>
            </ProtectedRoute>
          }
        />

        <Route
          path="/profile/:username/friends"
          element={
            <ProtectedRoute>
              <Layout>
                <Profile />
              </Layout>
            </ProtectedRoute>
          }
        />

        <Route
          path="/friends"
          element={
            <ProtectedRoute>
              <Layout>
                <Friends />
              </Layout>
            </ProtectedRoute>
          }
        />

        <Route
          path="/chat"
          element={
            <ProtectedRoute>
              <Layout>
                <Chat />
              </Layout>
            </ProtectedRoute>
          }
        />

        <Route
          path="/chat/:conversationId"
          element={
            <ProtectedRoute>
              <Layout>
                <Chat />
              </Layout>
            </ProtectedRoute>
          }
        />

        <Route
          path="/chat-link/:messageId"
          element={
            <ProtectedRoute>
              <Layout>
                <Chat />
              </Layout>
            </ProtectedRoute>
          }
        />

        <Route
          path="/post/:postId"
          element={
            <ProtectedRoute>
              <Layout>
                <Post />
              </Layout>
            </ProtectedRoute>
          }
        />

        <Route
          path="/search"
          element={
            <ProtectedRoute>
              <Layout>
                <Search />
              </Layout>
            </ProtectedRoute>
          }
        />

        <Route
          path="/notifications"
          element={
            <ProtectedRoute>
              <Layout>
                <Notifications />
              </Layout>
            </ProtectedRoute>
          }
        />

        {/* Admin Routes */}
        <Route
          path="/admin"
          element={
            <ProtectedRoute>
              <AdminProtectedRoute>
                <AdminLayout>
                  <AdminDashboard />
                </AdminLayout>
              </AdminProtectedRoute>
            </ProtectedRoute>
          }
        />

        <Route
          path="/admin/users"
          element={
            <ProtectedRoute>
              <AdminProtectedRoute>
                <AdminLayout>
                  <AdminUsers />
                </AdminLayout>
              </AdminProtectedRoute>
            </ProtectedRoute>
          }
        />

        <Route
          path="/admin/users/:userId"
          element={
            <ProtectedRoute>
              <AdminProtectedRoute>
                <AdminLayout>
                  <UserDetail />
                </AdminLayout>
              </AdminProtectedRoute>
            </ProtectedRoute>
          }
        />

        <Route
          path="/admin/groups"
          element={
            <ProtectedRoute>
              <AdminProtectedRoute>
                <AdminLayout>
                  <AdminGroups />
                </AdminLayout>
              </AdminProtectedRoute>
            </ProtectedRoute>
          }
        />

        <Route path="*" element={<Navigate to="/" replace />} />
      </Routes>
      <FloatingVideoPlayer />
      {/* CallKit — audio call overlay, visible on all pages when user is in a call */}
      <ChatKit />
    </>
  );
}

function App() {
  return (
    <AuthProvider>
      <NotificationProvider>
        <AdminProvider>
          <ChatProvider>
            <FloatingVideoProvider>
              <ToastProvider>
                <BrowserRouter>
                  <AppRoutes />
                </BrowserRouter>
              </ToastProvider>
            </FloatingVideoProvider>
          </ChatProvider>
        </AdminProvider>
      </NotificationProvider>
    </AuthProvider>
  );
}

export default App;
